import supabase from './supabase'
import { trackApiCall } from './analytics'

interface ApiOptions extends RequestInit {
  skipAuth?: boolean // Optional flag to skip authentication (for APIs that don't require auth)
  skipTracking?: boolean // Optional flag to skip API tracking (for high-frequency or internal calls)
}

/**
 * Unified API client that automatically adds Supabase authentication token
 */
export class ApiClient {
  private static async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.access_token) {
        throw new Error('No valid session found. Please sign in.')
      }
      
      return {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      }
    } catch (error) {
      throw new Error('Authentication required. Please sign in.')
    }
  }

  /**
   * Send authenticated API request
   */
  static async fetch(url: string, options: ApiOptions = {}): Promise<Response> {
    const { skipAuth = false, skipTracking = false, headers = {}, ...restOptions } = options
    const method = (restOptions.method || 'GET').toUpperCase()
    const startTime = Date.now()

    let requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers as Record<string, string>
    }

    // If not skipping auth, add Authorization header
    if (!skipAuth) {
      const authHeaders = await this.getAuthHeaders()
      requestHeaders = { ...requestHeaders, ...authHeaders }
    }

    try {
      const response = await fetch(url, {
        ...restOptions,
        headers: requestHeaders
      })

      // If 401 error, token might be expired
      if (response.status === 401 && !skipAuth) {
        // Try to refresh session
        const { error } = await supabase.auth.refreshSession()
        if (error) {
          // Track the auth error before throwing
          if (!skipTracking) {
            trackApiCall(url, method, response, undefined, startTime)
          }
          // Redirect to login page with current URL as redirect param
          if (typeof window !== 'undefined') {
            const currentUrl = window.location.pathname + window.location.search
            window.location.href = `/?redirect=${encodeURIComponent(currentUrl)}`
            return new Response(null, { status: 401 })
          }
          throw new Error('Session expired. Please sign in again.')
        }

        // Get new headers and retry
        const newAuthHeaders = await this.getAuthHeaders()
        const retryResponse = await fetch(url, {
          ...restOptions,
          headers: {
            ...requestHeaders,
            ...newAuthHeaders
          }
        })

        // Track the retry response
        if (!skipTracking) {
          trackApiCall(url, method, retryResponse, undefined, startTime)
        }

        // If retry also returns 401, redirect to login
        if (retryResponse.status === 401 && typeof window !== 'undefined') {
          const currentUrl = window.location.pathname + window.location.search
          window.location.href = `/?redirect=${encodeURIComponent(currentUrl)}`
          return new Response(null, { status: 401 })
        }

        return retryResponse
      }

      // Track the successful or error response
      if (!skipTracking) {
        trackApiCall(url, method, response, undefined, startTime)
      }

      return response
    } catch (error) {
      // Track network errors
      if (!skipTracking) {
        trackApiCall(url, method, undefined, error as Error, startTime)
      }
      throw error
    }
  }

  /**
   * GET request
   */
  static async get(url: string, options: ApiOptions = {}): Promise<Response> {
    return this.fetch(url, { ...options, method: 'GET' })
  }

  /**
   * POST request
   */
  static async post(url: string, data?: any, options: ApiOptions = {}): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  /**
   * PUT request
   */
  static async put(url: string, data?: any, options: ApiOptions = {}): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  /**
   * DELETE request
   */
  static async delete(url: string, options: ApiOptions = {}): Promise<Response> {
    return this.fetch(url, { ...options, method: 'DELETE' })
  }

  /**
   * PATCH request
   */
  static async patch(url: string, data?: any, options: ApiOptions = {}): Promise<Response> {
    return this.fetch(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  /**
   * POST request for file upload (FormData)
   */
  static async postForm(url: string, formData: FormData, options: ApiOptions = {}): Promise<Response> {
    const { skipAuth = false, skipTracking = false, headers = {}, ...restOptions } = options
    const startTime = Date.now()

    let requestHeaders: Record<string, string> = {
      ...headers as Record<string, string>
    }

    // Don't set Content-Type, let browser automatically set multipart/form-data boundary
    if (!skipAuth) {
      const authHeaders = await this.getAuthHeaders()
      // Remove Content-Type because FormData needs browser to set it automatically
      const { 'Content-Type': _, ...authHeadersWithoutContentType } = authHeaders
      requestHeaders = { ...requestHeaders, ...authHeadersWithoutContentType }
    }

    try {
      const response = await fetch(url, {
        ...restOptions,
        method: 'POST',
        headers: requestHeaders,
        body: formData
      })

      // Track the response
      if (!skipTracking) {
        trackApiCall(url, 'POST', response, undefined, startTime)
      }

      return response
    } catch (error) {
      // Track network errors
      if (!skipTracking) {
        trackApiCall(url, 'POST', undefined, error as Error, startTime)
      }
      throw error
    }
  }
}

// Export convenience function
export const apiClient = ApiClient 
